import React, { useMemo, useCallback, useContext, useState } from "react";
import _ from "lodash";
import { Flex } from "reflexbox";
import styled, { useTheme } from "styled-components";
import { Heading, Body } from "../../UI/Text";
import moment from "moment";
import Table from "../../UI/Table";
import Button from "../../UI/Button";
import Modal from "../../UI/Modal";
import Card from "../../UI/Card";
import * as quoteReqs from "../../../requests/quoteManagementRequests";
import formatter from "../../../modules/formatter";
import { useQuery } from "../../Hooks";
import { getQuotes } from "../../../requests/quoteManagementRequests";
import UserContext from "../../../context/user/UserContext";
import Loading from "../../UI/Loading";
import AlertContext from "../../../context/alert/AlertContext";
import { useNavigate } from "react-router-dom";

const ButtonsWrapper = styled(Flex)`
  justify-content: center;
  align-items: center;
  transition: 200ms all ease-out;
`;

const { copyQuote, deleteQuote } = quoteReqs;

const QuoteTable = (props) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { activeUser, pid } = useContext(UserContext);
  const { showFailureAlert, showSuccessAlert, showWarningAlert } =
    useContext(AlertContext);
  const { groupId, roleLevel, username } = activeUser || {};

  const {
    onRowClick,
    allQuotes = false,
    isLoading,
    createLink,
    createButtonText,
    createButtonIcon,
    hideCreate = true,
    isQuoteRequest = false,
  } = props || {};

  const queryID1 = allQuotes ? "allQuotes" : "quotes";
  const queryID2 = "quoteRequests";

  const [isCopying, setIsCopying] = useState(false);
  const [show, setShow] = useState(false);
  const [currentQuote, setCurrentQuote] = useState(null);

  const quotes = useQuery({
    queryID: queryID1,
    request: getQuotes,
    queryVariables: {
      type: 1,
      groupId: allQuotes ? undefined : groupId || undefined,
      username: allQuotes
        ? undefined
        : !groupId
        ? activeUser?.username
        : undefined,
      pid,
    },
    skip: isQuoteRequest || isLoading || !pid || !activeUser,
  });
  const {
    data: qData,
    refetchQuery: qRefetch,
    setData: qSetData,
    isLoading: qLoading,
  } = quotes || {};

  const requests = useQuery({
    queryID: queryID2,
    request: getQuotes,
    queryVariables: {
      type: 3,
      groupId: (allQuotes || roleLevel) && groupId === 0 ? undefined : groupId,
      pid,
      username: !groupId ? username : undefined,
    },
    skip:
      (roleLevel < 5 && roleLevel !== 0) ||
      !isQuoteRequest ||
      allQuotes ||
      isLoading ||
      !activeUser ||
      (!isQuoteRequest && (qLoading || !qData)) ||
      !pid,
  });

  const {
    data: rData,
    refetchQuery: rRefetch,
    setData: rSetData,
    isLoading: rLoading,
  } = requests || {};

  const { rows: qRows = [], count: qCount } = qData || {};
  const { rows: rRows = [], count: rCount } = rData || {};

  const stopPropagation = (event) => {
    event.stopPropagation();
  };

  const onDealerPricingClick = useCallback(() => {
    setCurrentQuote(null);
    navigate(`/DealerQuote/${currentQuote}`);
  }, [currentQuote, navigate]);

  const onDeleteQuote = useCallback(
    async (event, quoteId) => {
      stopPropagation(event);
      const inQuotes = !!_.find(qRows, (quote) => quote.id === quoteId);
      const rows = inQuotes ? qRows : rRows;
      const setData = inQuotes ? qSetData : rSetData;

      const newQuotes = [...rows];
      _.remove(newQuotes, (quote) => quote.id === quoteId);

      setData({
        count: (isQuoteRequest ? rCount : qCount) - 1,
        rows: newQuotes,
      });

      await deleteQuote({ id: quoteId });
    },
    [qRows, rRows, qSetData, rSetData, isQuoteRequest, rCount, qCount]
  );

  const onCopyQuote = useCallback(
    async (event, quoteId) => {
      stopPropagation(event);
      try {
        setIsCopying(quoteId);
        showWarningAlert(`Copying Quote #${quoteId}...`);
        const inQuotes = !!_.find(qRows, (quote) => quote.id === quoteId);
        const rows = inQuotes ? qRows : rRows;
        const setData = inQuotes ? qSetData : rSetData;

        const newQuote = await copyQuote({ id: quoteId });

        const newQuotes = [newQuote, ...rows];
        setData({
          count: newQuotes.length,
          rows: newQuotes,
        });
        setIsCopying(false);
        showSuccessAlert(
          `New Quote #${newQuote.id} copied from Quote #${quoteId}.`
        );
      } catch (error) {
        console.error(error);
        showFailureAlert(`${error}`);
      }
    },
    [
      qRows,
      qSetData,
      rRows,
      rSetData,
      showFailureAlert,
      showSuccessAlert,
      showWarningAlert,
    ]
  );

  const columns = useMemo(
    () => [
      {
        id: "id",
        data: (row) => (
          <Body center ellipsis>
            {row.id}
          </Body>
        ),
        header: "#",
        size: { md: 1, sm: 1, xs: -1 },
      },
      {
        id: "jobName",
        data: (row) => (
          <Body center>{row.jobName}</Body>          
        ),
        header: "Job Name",
        size: { md: 3, sm: 3, xs: 2 },
      },
      {
        id: "customerName",
        data: (row) => {
          const { lastName, firstName, customerName } = row;

          return (
            <Body center ellipsis>
              {firstName || lastName
                ? `${firstName || ""}${lastName && firstName ? " " : ""}${
                    lastName || " "
                  }`
                : customerName}
            </Body>
          );
        },
        header: "Customer",
        size: { md: 3, sm: 3, xs: -1 },
      },

      {
        id: "createdByFirstName",
        data: (row) => (
          <Body ellipsis center>
            {row?.createdByFirstName} {row?.createdByLastName}
          </Body>
        ),
        header: "Created By",
        size: { md: 3, sm: 0, xs: 0 },
      },
      {
        id: "createdDate",
        data: (row) => (
          <Body center>{`${moment(row.createdDate).format(
            "MM/DD/YYYY"
          )}`}</Body>
          // <Body center>{`${row.createdDate}`}</Body>
        ),
        header: "Created On",
        size: { md: 3, sm: 3, xs: 2 },
      },

      {
        id: "totalCost",
        data: (row) => (
          <Body right>{`$${formatter.currency(row.totalCost)}`}</Body>
        ),
        header: "Quote Value",
        size: { md: 3, sm: 3, xs: 3 },
      },
      {
        display: (props) => {
          let quote = props.cell.row.original;

          return (
            <ButtonsWrapper id="actions">
              <Button
                size="small"
                reverse
                color={theme.secondary}
                title="Edit Quote"
                iconName="faPencil"
                onClick={(e) => onRowClick(props.cell.row, e)}
              />
              {isCopying === props.cell.row.original.id ? (
                <Loading inline />
              ) : (
                <Button
                  size="small"
                  color={theme.success}
                  reverse
                  title="Copy Quote"
                  iconName="faCopy"
                  onClick={(e) => onCopyQuote(e, quote.id)}
                />
              )}
              <Button
                size="small"
                reverse
                title="Delete Quote"
                color={theme.danger}
                iconName="faTrashCan"
                onClick={(e) => onDeleteQuote(e, quote.id)}
              />
            </ButtonsWrapper>
          );
        },
        header: "",
        sort: false,
        size: { md: 5, sm: 0, xs: 0 },
      },
    ],
    [
      theme.secondary,
      theme.success,
      theme.danger,
      isCopying,
      onRowClick,
      onCopyQuote,
      onDeleteQuote,
    ]
  );

  // if (isQuoteRequest && (rRows?.length === 0 || qRows?.length === 0))
  //   return null;

  return (
    <>
      <Modal show={show} setShow={setShow}>
        <Card maxWidth="400px">
          <Heading bold center color={theme.danger}>
            INTERNAL DOCUMENTS
          </Heading>
          <Heading my="1rem" center>
            Are you sure you want to view this page?
          </Heading>
          <Body center>
            This page contains sensitive internal documents, are you sure you
            wish to proceed?
          </Body>
          <Flex my="1rem">
            <Button
              iconName="faBan"
              color={theme.danger}
              onClick={() => setShow(false)}
            >
              Go Back
            </Button>
            <Button
              color={theme.success}
              iconName="faCheck"
              onClick={onDealerPricingClick}
            >
              Continue
            </Button>
          </Flex>
        </Card>
      </Modal>
      {!allQuotes ? (
        <Table
          title={isQuoteRequest ? "Quote Requests" : "Quotes"}
          hideCreate={hideCreate}
          hideBack
          createLink={createLink}
          createButtonIcon={createButtonIcon}
          createButtonText={createButtonText}
          hideToggle
          refetchData={isQuoteRequest ? rRefetch : qRefetch}
          hideRefetch={false}
          data={isQuoteRequest ? rRows : qRows}
          columns={columns}
          showAddNew
          isLoading={isQuoteRequest ? rLoading : qLoading}
          totalCount={isQuoteRequest ? rCount : qCount}
          onRowClick={onRowClick}
        />
      ) : (
        <Table
          title="All Quotes"
          createLink={createLink}
          createButtonIcon="faFileCirclePlus"
          createButtonText={createButtonText}
          hideToggle
          refetchData={qRefetch}
          hideRefetch={false}
          data={qRows}
          columns={columns}
          showAddNew
          isLoading={qLoading}
          totalCount={qCount}
          onRowClick={onRowClick}
        />
      )}
    </>
  );
};

export default (props) => <QuoteTable {...props} />;
